import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Clock,
  CheckCircle2,
  Circle,
  ArrowLeft,
  ArrowRight,
  PlayCircle,
  Award,
} from "lucide-react";
import AppHeader from "../components/AppHeader";
import QuizBlock from "../components/QuizBlock";
import BookmarkToggle from "../components/BookmarkToggle";
import SearchOverlay from "../components/SearchOverlay";
import BookmarksPanel from "../components/BookmarksPanel";
import { Button } from "../components/ui/button";
import { useCourse } from "../context/CourseContext";
import { useUser } from "../context/UserContext";

export default function ModuleReader() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { course, loading } = useCourse();
  const { user, completeTopic } = useUser();
  const [searchOpen, setSearchOpen] = useState(false);
  const [bookmarksOpen, setBookmarksOpen] = useState(false);

  const module = useMemo(() => course?.modules.find((m) => m.id === id), [course, id]);
  const moduleIndex = useMemo(
    () => (course ? course.modules.findIndex((m) => m.id === id) : -1),
    [course, id]
  );
  const nextModule = course && moduleIndex >= 0 ? course.modules[moduleIndex + 1] : null;
  const prevModule = course && moduleIndex > 0 ? course.modules[moduleIndex - 1] : null;

  const completedSet = useMemo(() => new Set(user?.completed_topics || []), [user]);
  const completedInModule = module?.topics.filter((t) => completedSet.has(t.id)).length || 0;
  const totalInModule = module?.topics.length || 0;
  const moduleDone = totalInModule > 0 && completedInModule >= totalInModule;

  useEffect(() => {
    const hash = window.location.hash?.slice(1);
    if (hash) {
      setTimeout(() => {
        const el = document.getElementById(hash);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 300);
    } else {
      window.scrollTo({ top: 0 });
    }
  }, [id]);

  if (loading || !course) {
    return (
      <div className="min-h-screen bg-background">
        <AppHeader onOpenSearch={() => setSearchOpen(true)} onOpenBookmarks={() => setBookmarksOpen(true)} />
        <div className="p-10 text-muted-foreground">Loading…</div>
      </div>
    );
  }

  if (!module) {
    return (
      <div className="min-h-screen bg-background">
        <AppHeader onOpenSearch={() => setSearchOpen(true)} onOpenBookmarks={() => setBookmarksOpen(true)} />
        <div className="p-10">
          <p className="text-muted-foreground">Module not found.</p>
          <Button onClick={() => navigate("/home")} className="mt-4 rounded-full">
            Back to home
          </Button>
        </div>
      </div>
    );
  }

  const handleMarkComplete = async (topicId) => {
    await completeTopic(module.id, topicId);
  };

  return (
    <div className="min-h-screen bg-background" data-testid={`module-reader-${module.id}`}>
      <AppHeader
        onOpenSearch={() => setSearchOpen(true)}
        onOpenBookmarks={() => setBookmarksOpen(true)}
      />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pb-32">
        {/* Module header */}
        <section className="pt-10 pb-8 border-b border-border">
          <button
            onClick={() => navigate("/home")}
            data-testid="back-to-home-link"
            className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.22em] text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="h-3 w-3" />
            All modules
          </button>

          <div className="mt-4 flex items-center gap-3">
            <span
              className="inline-flex items-center justify-center h-8 w-8 rounded-full text-sm font-semibold"
              style={{ background: `${module.accent}1a`, color: module.accent }}
            >
              {module.number}
            </span>
            <span className="text-xs uppercase tracking-[0.22em] text-muted-foreground">
              Module {module.number} of {course.modules.length}
            </span>
            <span
              className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full"
              style={{ background: `${module.accent}14`, color: module.accent }}
            >
              <Clock className="h-3 w-3" />
              {module.hours} hr{module.hours > 1 ? "s" : ""}
            </span>
          </div>

          <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-balance">
            {module.title}
            <span className="block italic text-brand-amber text-xl sm:text-2xl lg:text-3xl mt-2 not-italic font-sans font-normal text-muted-foreground">
              {module.subtitle}
            </span>
          </h1>

          <p className="mt-5 max-w-3xl text-muted-foreground text-base sm:text-lg leading-relaxed">
            <span className="text-foreground font-medium">Key takeaway. </span>
            {module.takeaway}
          </p>

          <div className="mt-6 text-xs text-muted-foreground">
            {completedInModule}/{totalInModule} topics completed in this module
          </div>
        </section>

        {/* Real-world story video */}
        <section className="mt-10">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-brand-amber font-semibold">
            <PlayCircle className="h-4 w-4" />
            Real-world story · 60 seconds
          </div>
          <h2 className="mt-2 font-display text-2xl sm:text-3xl">
            How this module shows up in everyday life
          </h2>
          <p className="mt-3 text-muted-foreground leading-relaxed max-w-3xl">{module.story}</p>

          <div
            className="mt-6 relative w-full aspect-video rounded-2xl overflow-hidden border border-border bg-muted"
            data-testid={`module-video-${module.id}`}
          >
            <iframe
              className="absolute inset-0 w-full h-full"
              src={`https://www.youtube-nocookie.com/embed/${module.youtube_id}?rel=0&modestbranding=1`}
              title={`${module.title} — intro video`}
              loading="lazy"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </section>

        {/* Topics */}
        <section className="mt-14 grid grid-cols-1 lg:grid-cols-[220px_minmax(0,1fr)] gap-10">
          <aside className="lg:sticky lg:top-24 self-start hidden lg:block">
            <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground mb-4">
              In this module
            </div>
            <nav className="space-y-1">
              {module.topics.map((t) => {
                const done = completedSet.has(t.id);
                return (
                  <a
                    key={t.id}
                    href={`#${t.id}`}
                    className="block px-3 py-2 rounded-lg text-sm hover:bg-muted transition-colors"
                    data-testid={`toc-link-${t.id}`}
                  >
                    <span className="flex items-center gap-2">
                      {done ? (
                        <CheckCircle2 className="h-3.5 w-3.5 text-brand-amber shrink-0" />
                      ) : (
                        <Circle className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      )}
                      <span className="truncate">{t.title}</span>
                    </span>
                  </a>
                );
              })}
            </nav>
          </aside>

          <div className="max-w-[72ch]">
            {module.topics.map((t, idx) => {
              const done = completedSet.has(t.id);
              return (
                <article key={t.id} id={t.id} className="pt-8 first:pt-0 scroll-mt-24" data-testid={`topic-${t.id}`}>
                  <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div>
                      <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground">
                        Topic {idx + 1}
                      </div>
                      <h3 className="font-display text-3xl sm:text-4xl mt-1 text-balance">
                        {t.title}
                      </h3>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                        <Clock className="h-3.5 w-3.5" />
                        {t.reading_time} min read
                      </span>
                      <BookmarkToggle topicId={t.id} />
                    </div>
                  </div>

                  <div className="mt-6 space-y-5">
                    {t.blocks.map((b, bi) => {
                      if (b.type === "h")
                        return (
                          <h4
                            key={bi}
                            className="font-display text-xl sm:text-2xl text-brand-amber mt-8"
                          >
                            {b.text}
                          </h4>
                        );
                      if (b.type === "callout")
                        return (
                          <div
                            key={bi}
                            className="relative pl-5 py-2 my-2 border-l-[3px] border-brand-amber/70 text-base italic text-foreground/90"
                          >
                            {b.text}
                          </div>
                        );
                      return (
                        <p
                          key={bi}
                          className="text-base leading-[1.75] text-foreground/85"
                        >
                          {b.text}
                        </p>
                      );
                    })}
                  </div>

                  <QuizBlock key={t.id} moduleId={module.id} topic={t} />

                  <div className="mt-8 flex flex-wrap items-center gap-3 pb-10 border-b border-border">
                    <Button
                      onClick={() => handleMarkComplete(t.id)}
                      disabled={done}
                      data-testid={`mark-complete-${t.id}`}
                      className="rounded-full bg-brand-amber hover:bg-brand-amber-hover text-white dark:text-brand-ink"
                    >
                      {done ? (
                        <>
                          <CheckCircle2 className="h-4 w-4 mr-2" />
                          Completed
                        </>
                      ) : (
                        <>Mark topic complete</>
                      )}
                    </Button>
                    {idx < module.topics.length - 1 && (
                      <a
                        href={`#${module.topics[idx + 1].id}`}
                        className="text-sm text-muted-foreground hover:text-foreground"
                      >
                        Next topic ↓
                      </a>
                    )}
                  </div>
                </article>
              );
            })}

            {/* Module end navigation */}
            <div className="mt-12 rounded-2xl border border-border bg-card p-6 sm:p-8">
              {moduleDone ? (
                <>
                  <div className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-brand-amber font-semibold">
                    <Award className="h-4 w-4" />
                    Module complete
                  </div>
                  <h3 className="font-display text-2xl sm:text-3xl mt-2">
                    Great work. You finished {module.title}.
                  </h3>
                </>
              ) : (
                <>
                  <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground">
                    Keep going
                  </div>
                  <h3 className="font-display text-2xl sm:text-3xl mt-2">
                    {completedInModule}/{totalInModule} topics done in this module
                  </h3>
                </>
              )}
              <div className="mt-5 flex flex-wrap gap-3">
                {prevModule && (
                  <Button
                    variant="outline"
                    onClick={() => navigate(`/module/${prevModule.id}`)}
                    className="rounded-full"
                    data-testid="prev-module-btn"
                  >
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    {prevModule.title}
                  </Button>
                )}
                {nextModule ? (
                  <Button
                    onClick={() => navigate(`/module/${nextModule.id}`)}
                    className="rounded-full bg-brand-amber hover:bg-brand-amber-hover text-white dark:text-brand-ink"
                    data-testid="next-module-btn"
                  >
                    {nextModule.title}
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                ) : (
                  <Button
                    onClick={() => navigate("/home")}
                    className="rounded-full bg-brand-amber hover:bg-brand-amber-hover text-white dark:text-brand-ink"
                    data-testid="finish-course-btn"
                  >
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    Finish Course
                  </Button>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>

      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />
      <BookmarksPanel open={bookmarksOpen} onClose={() => setBookmarksOpen(false)} />
    </div>
  );
}
